import * as React from "react";

import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils/cn";

/** Multiline text control matching the design tokens used by <Input>. */
export const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<"textarea">
>(({ className, ...props }, ref) => (
  <textarea
    ref={ref}
    className={cn(
      "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
      "aria-[invalid=true]:border-destructive",
      className,
    )}
    {...props}
  />
));
Textarea.displayName = "Textarea";

/** Native select styled to match the form kit. */
export const Select = React.forwardRef<
  HTMLSelectElement,
  React.ComponentProps<"select">
>(({ className, children, ...props }, ref) => (
  <select
    ref={ref}
    className={cn(
      "flex min-h-touch w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
      className,
    )}
    {...props}
  >
    {children}
  </select>
));
Select.displayName = "Select";

interface CheckboxFieldProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

/** Accessible labelled checkbox for multiselect lists (sectors, characteristics). */
export function CheckboxField({
  id,
  label,
  checked,
  onChange,
  disabled,
}: CheckboxFieldProps) {
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-center gap-2 rounded-md border border-input px-3 py-2 text-sm has-[:checked]:border-primary has-[:checked]:bg-primary/5"
    >
      <input
        id={id}
        type="checkbox"
        className="size-4 accent-primary"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}

interface FieldShellProps {
  label: React.ReactNode;
  htmlFor?: string;
  description?: string;
  labelClassName?: string;
  children: React.ReactNode;
}

/** Label + optional description wrapper for a non-text control. */
export function FieldShell({
  label,
  htmlFor,
  description,
  labelClassName,
  children,
}: FieldShellProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className={labelClassName}>
        {label}
      </Label>
      {children}
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
    </div>
  );
}

/** One of the four sustainability hues used to accent a form section header. */
export type SectionTone = "environment" | "economic" | "social" | "governance";

const SECTION_TONE: Record<SectionTone, string> = {
  environment: "bg-theme-environment-soft text-theme-environment-ink",
  economic: "bg-theme-economic-soft text-theme-economic-ink",
  social: "bg-theme-social-soft text-theme-social-ink",
  governance: "bg-theme-governance-soft text-theme-governance-ink",
};

interface FormSectionProps {
  /** A lucide icon element; sized by the section header. */
  icon: React.ReactNode;
  tone: SectionTone;
  title: string;
  description?: string;
  /** Optional right-aligned header slot (e.g. an autosave status). */
  aside?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * A titled card grouping related profile fields. The icon tile is tinted with
 * one of the four sustainability hues so the profile form finally carries the
 * app's colour identity instead of reading as a flat wall of inputs.
 */
export function FormSection({
  icon,
  tone,
  title,
  description,
  aside,
  children,
}: FormSectionProps) {
  return (
    <section className="rounded-xl border border-border bg-card p-6 shadow-sm">
      <div className="mb-5 flex items-center gap-3">
        <span
          aria-hidden
          className={cn(
            "flex size-10 flex-none items-center justify-center rounded-[11px] [&_svg]:size-5",
            SECTION_TONE[tone],
          )}
        >
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-bold leading-tight tracking-tight">
            {title}
          </h3>
          {description && (
            <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}
